// frontend/src/components/Notifications/NotificationBell.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Bell, BellOff, X, AlertCircle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // ========== توابع ارتباط با API ==========

  // دریافت تعداد نوتیفیکیشن‌های نخوانده
  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/notifications/notifications/unread_count/');
      setUnreadCount(response.data.unread_count || 0);
      return response.data.unread_count;
    } catch (err) {
      console.error('Error fetching unread count:', err);
      // اگر API کار نکرد، از localStorage استفاده کن
      const saved = localStorage.getItem('mock_notifications');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const count = parsed.filter(n => !n.is_read).length;
          setUnreadCount(count);
          return count;
        } catch (e) {}
      }
      return 0;
    }
  };

  // دریافت لیست نوتیفیکیشن‌ها
  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/notifications/notifications/', {
        params: { limit: 20 }
      });
      
      // تبدیل داده‌های API به فرمت مورد نیاز
      const data = response.data.results || response.data || [];
      const formattedData = data.map(item => ({
        id: item.id,
        title: item.title,
        message: item.message,
        type: { name: item.type_name || 'سیستمی' },
        priority: item.priority || 'medium',
        is_read: item.is_read || false,
        created_at: item.created_at,
        link: item.link || '#'
      }));
      
      setNotifications(formattedData);
      // ذخیره در localStorage برای حالت آفلاین
      localStorage.setItem('mock_notifications', JSON.stringify(formattedData));
      return formattedData;
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('خطا در دریافت اعلان‌ها');
      
      // اگر API کار نکرد، از localStorage استفاده کن
      const saved = localStorage.getItem('mock_notifications');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setNotifications(parsed);
          return parsed;
        } catch (e) {}
      }
      return [];
    } finally {
      setLoading(false);
    }
  };

  // علامت‌گذاری به عنوان خوانده شده
  const markAsRead = async (id) => {
    try {
      await api.post(`/notifications/notifications/${id}/mark_read/`);
      
      // به‌روزرسانی لیست محلی
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // به‌روزرسانی localStorage
      const updated = notifications.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      );
      localStorage.setItem('mock_notifications', JSON.stringify(updated));
      
    } catch (err) {
      console.error('Error marking as read:', err);
      // در صورت خطا، عملیات را محلی انجام بده
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  // علامت‌گذاری همه به عنوان خوانده شده
  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/notifications/mark_all_read/');
      
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
      
      const updated = notifications.map(n => ({ ...n, is_read: true }));
      localStorage.setItem('mock_notifications', JSON.stringify(updated));
      
    } catch (err) {
      console.error('Error marking all as read:', err);
      // در صورت خطا، عملیات را محلی انجام بده
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
    }
  };

  // حذف نوتیفیکیشن
  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/notifications/${id}/`);
      
      setNotifications(prev => prev.filter(n => n.id !== id));
      const item = notifications.find(n => n.id === id);
      if (item && !item.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      const updated = notifications.filter(n => n.id !== id);
      localStorage.setItem('mock_notifications', JSON.stringify(updated));
      
    } catch (err) {
      console.error('Error deleting notification:', err);
      // در صورت خطا، عملیات را محلی انجام بده
      setNotifications(prev => prev.filter(n => n.id !== id));
      const item = notifications.find(n => n.id === id);
      if (item && !item.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    }
  };

  // ========== useEffect ها ==========

  // بارگذاری اولیه
  useEffect(() => {
    // ابتدا از localStorage بارگذاری کن
    const saved = localStorage.getItem('mock_notifications');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setNotifications(parsed);
        setUnreadCount(parsed.filter(n => !n.is_read).length);
      } catch (e) {}
    }
    
    // سپس از API دریافت کن
    fetchUnreadCount();
    fetchNotifications();
    
    // بارگذاری خودکار هر 30 ثانیه
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // بستن دراپ‌داون با کلیک خارج
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ========== توابع کمکی ==========

  const formatTime = (date) => {
    const now = new Date();
    const diff = (now - new Date(date)) / 1000;
    if (diff < 60) return 'لحظاتی پیش';
    if (diff < 3600) return `${Math.floor(diff / 60)} دقیقه پیش`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ساعت پیش`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} روز پیش`;
    return new Date(date).toLocaleDateString('fa-IR');
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-500',
      medium: 'text-blue-500',
      high: 'text-orange-500',
      urgent: 'text-red-500'
    };
    return colors[priority] || 'text-gray-500';
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'urgent') return <AlertCircle className="w-4 h-4" />;
    if (priority === 'high') return <Clock className="w-4 h-4" />;
    return null;
  };

  // باز کردن دراپ‌داون و بارگذاری مجدد
  const handleToggle = async () => {
    if (!isOpen) {
      await fetchNotifications();
      await fetchUnreadCount();
    }
    setIsOpen(!isOpen);
  };

  // ========== رندر ==========

  return (
    <div className="relative" ref={dropdownRef}>
      {/* دکمه زنگوله */}
      <button
        onClick={handleToggle}
        className="relative p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
        aria-label="اعلان‌ها"
      >
        <Bell className="w-6 h-6 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full shadow-lg">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* دراپ‌داون */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-[420px] max-w-[95vw] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 max-h-[560px] flex flex-col overflow-hidden">
          {/* هدر */}
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-gray-900">اعلان‌ها</h3>
              {unreadCount > 0 && (
                <span className="text-xs bg-blue-600 text-white px-2.5 py-0.5 rounded-full">
                  {unreadCount} جدید
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  خواندن همه
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* لیست */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                <span className="text-sm">در حال بارگذاری...</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12 text-red-500">
                <AlertCircle className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">{error}</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <BellOff className="w-14 h-14 mb-3 opacity-30" />
                <p className="text-sm font-medium">هیچ اعلانی وجود ندارد</p>
                <p className="text-xs mt-1">با ثبت سفارش یا فعالیت جدید، اعلان‌ها اینجا نمایش داده می‌شوند</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-all ${
                      !n.is_read ? 'bg-blue-50/60 border-r-4 border-blue-500' : ''
                    }`}
                    onClick={() => {
                      if (n.link && n.link !== '#') {
                        window.location.href = n.link;
                      }
                      if (!n.is_read) {
                        markAsRead(n.id);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${getPriorityColor(n.priority)} bg-gray-100`}>
                        {getPriorityIcon(n.priority) || <Bell className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <h4 className="text-sm font-medium text-gray-900">{n.title}</h4>
                          {!n.is_read && <span className="w-2 h-2 mt-1.5 bg-blue-600 rounded-full"></span>}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{n.message}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-xs text-gray-400">{formatTime(n.created_at)}</span>
                          <div className="flex items-center gap-1">
                            {!n.is_read && (
                              <button
                                onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                خوانده شد
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                              className="text-xs text-gray-400 hover:text-red-500"
                            >
                              حذف
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* فوتر */}
          <div className="p-3 border-t border-gray-100 text-center bg-gray-50/80">
            <Link
              to="/notifications"
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              onClick={() => setIsOpen(false)}
            >
              مشاهده همه اعلان‌ها
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;